/**
 * RiskEventEngine — the event catalog.
 *
 * Every event is grounded in real bed-in-a-box industry history (2014–2026):
 * China AD/CVD duty cycles, Section 301 reviews, oil shocks, Meta CAC
 * inflation, Amazon policy churn, viral return horror stories, and the
 * great 2015–2017 brand explosion. Weights gate eligibility by world state;
 * scripted one-time events anchor the 2024–2026 timeline.
 *
 * Events express their effects through:
 *  1. Immediate mutations (cash, tariffs, awareness, reviews…)
 *  2. Timed `Modifier`s consumed by the economy engine
 *  3. Player choices with real trade-offs (spend cash vs absorb pain)
 */

import { clamp } from './format';
import { has } from './research';
import type {
  ActiveEvent,
  EventCategory,
  EventChoiceDef,
  EventDef,
  EventRuntime,
  GameState,
  MarketingChannelId,
  Modifier,
  ModifierField,
} from './types';
import { activeProducts } from './economy';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

let modSeq = 0;

export function pushMod(
  s: GameState,
  field: ModifierField,
  opts: { mult?: number; add?: number; days: number; label: string; source?: string },
): void {
  const m: Modifier = {
    id: `mod_${s.time.day}_${modSeq++}`,
    sourceEvent: opts.source,
    field,
    mult: opts.mult,
    add: opts.add,
    expiresDay: s.time.day + opts.days,
    label: opts.label,
  };
  s.modifiers.push(m);
}

function news(s: GameState, category: EventCategory | 'finance' | 'milestone', severity: 0 | 1 | 2 | 3 | 4 | 5, headline: string, detail?: string): void {
  s.news.unshift({
    id: `n_${s.time.day}_${modSeq++}`,
    day: s.time.day,
    category,
    severity,
    headline,
    detail,
  });
  if (s.news.length > 140) s.news.length = 140;
}

const always = () => 1;

// Convenience eligibility gates
const wAmazon = (s: GameState) => (s.channels.amazon.enabled && !s.channels.amazon.suspended ? 1 : 0);
const wShopifyScale = (s: GameState) => (s.player.awareness > 12 ? 1 : 0);
const wInfluencerSpend = (s: GameState) => (s.marketing.allocation.influencer > 0.08 ? 1 : 0);
const wChinaExposure = (s: GameState) => (s.suppliersUnlocked.includes('foshan-foamworks') ? 1 : 0);
const wWholesale = (s: GameState) => (s.channels.wholesale.enabled ? 1 : 0);

// ---------------------------------------------------------------------------
// Catalog
// ---------------------------------------------------------------------------

export const EVENTS: EventDef[] = [
  // ======================= TRADE & TARIFFS =======================
  {
    id: 'section301-review',
    title: 'Section 301 Quadrennial Review Concludes',
    body: 'USTR confirms continued Section 301 tariff layers on Chinese goods after the statutory review. Importers brace for another cost layer on China-origin mattresses.',
    category: 'trade',
    severity: 3,
    durationDays: 0,
    window: [110, 160],
    onceOnly: true,
    weight: always,
    apply: (s) => {
      s.tariffs.CN += 0.08;
      news(s, 'trade', 3, 'Section 301 tariffs maintained on China', 'Effective China duty burden rises to +8% on mattress COGS.');
    },
    choices: [
      {
        id: 'absorb',
        label: 'Absorb the increase',
        detail: 'Margin compression. No action.',
        apply: () => 'You absorb the duty increase. Margins tighten.',
      },
      {
        id: 'price',
        label: 'Raise prices ~4%',
        detail: 'Pass-through: -5% demand for 75 days.',
        apply: (s) => {
          for (const p of activeProducts(s)) p.price = Math.round(p.price * 1.04);
          pushMod(s, 'demandMult', { mult: 0.95, days: 75, label: 'Tariff pass-through pricing' });
          return 'Prices raised. Volume dips while customers comparison-shop.';
        },
      },
      {
        id: 'diversify',
        label: 'Fast-track supplier diversification',
        detail: '$28,000 — qualify backup lines now.',
        cost: 28_000,
        apply: (s) => {
          s.player.reputation = clamp(s.player.reputation + 2, 0, 100);
          if (s.suppliersUnlocked.includes('surabaya-comfort') || has(s, 'global-sourcing')) {
            s.tariffs.ID = Math.max(1, s.tariffs.ID - 0.03);
            return 'Indonesia line qualified early. Slightly better duty terms locked.';
          }
          s.tariffs.VN = Math.max(1, s.tariffs.VN - 0.05);
          return 'Vietnam line qualified with preferential terms (-5% duty exposure).';
        },
      },
    ],
  },
  {
    id: 'china-cvd-sunset',
    title: 'USITC Sunset Review: China Duties Stay',
    body: 'The five-year (sunset) review concludes with AD/CVD orders on Chinese mattresses continuing — the "all others" CVD rate remains punitive. China sourcing gets more expensive again.',
    category: 'trade',
    severity: 3,
    durationDays: 0,
    cooldownDays: 200,
    weight: (s) => wChinaExposure(s) * 3,
    apply: (s) => {
      s.tariffs.CN += 0.12;
    },
    choices: [
      {
        id: 'absorb',
        label: 'Absorb it',
        detail: 'China COGS +12% going forward.',
        apply: () => 'Duty increase absorbed. Your China landed cost just jumped.',
      },
      {
        id: 'pivot',
        label: 'Shift POs to Vietnam',
        detail: '$15,000 requalification. Lower duty exposure.',
        cost: 15_000,
        apply: (s) => {
          s.player.reputation = clamp(s.player.reputation + 1, 0, 100);
          return 'New POs will default to Vietnam. One trade risk swapped for another.';
        },
      },
    ],
  },
  {
    id: 'vn-ad-petition',
    title: 'AD Petition Filed Against Vietnamese Mattresses',
    body: 'A coalition files antidumping petitions targeting Vietnam-origin mattresses. Preliminary rates could be extreme. Every importer with Vietnam exposure is repricing risk today.',
    category: 'trade',
    severity: 4,
    durationDays: 0,
    cooldownDays: 240,
    weight: (s) => (s.time.day > 240 ? 3 : 0),
    apply: (s) => {
      s.tariffs.VN += 0.3;
    },
    choices: [
      {
        id: 'hold',
        label: 'Hold Vietnam lines',
        detail: 'Higher duties until the case resolves.',
        apply: () => 'You hold. Vietnam landed costs spike; margins take the hit.',
      },
      {
        id: 'indonesia',
        label: 'Emergency-shift to Indonesia',
        detail: '$18,000 — requires Global Sourcing Network.',
        cost: 18_000,
        apply: (s) => {
          if (!has(s, 'global-sourcing')) return 'No qualified Indonesian line — the shift fails. Money spent on airfreight samples.';
          s.tariffs.ID = Math.max(1, s.tariffs.ID - 0.02);
          pushMod(s, 'freightMult', { mult: 1.06, days: 40, label: 'Emergency lane setup' });
          return 'Indonesia absorbs the volume at a rush premium.';
        },
      },
      {
        id: 'nearshore',
        label: 'Accelerate nearshore pilot',
        detail: '$26,000 — fast-track Mexico qualification.',
        cost: 26_000,
        apply: (s) => {
          s.research.completed.push('nearshore-lane');
          s.suppliersUnlocked.push('monterrey-colchones');
          return 'Crisis speed-runs the Monterrey line. 13-day lead times unlocked.';
        },
      },
    ],
  },
  {
    id: 'cbp-origin-crackdown',
    title: 'CBP Cracks Down on Origin Fraud',
    body: 'Customs intensifies verification of "Made in Vietnam" claims after high-profile transshipment prosecutions. Documentation scrutiny adds cost and delay across Asian lanes.',
    category: 'regulatory',
    severity: 2,
    durationDays: 45,
    cooldownDays: 150,
    weight: (s) => wChinaExposure(s) * 2,
    apply: (s) => {
      const halved = s.team.hires.find((h) => h.id === 'compliance')?.hired;
      pushMod(s, 'cogsMult', {
        mult: halved ? 1.03 : 1.06,
        days: 45,
        label: 'Customs scrutiny & brokerage fees',
        source: 'cbp-origin-crackdown',
      });
      pushMod(s, 'freightMult', { mult: halved ? 1.02 : 1.05, days: 45, label: 'Port exam delays', source: 'cbp-origin-crackdown' });
    },
    choices: [
      {
        id: 'audit',
        label: 'Commission a supply-chain audit',
        detail: '$14,000 — bulletproof your origin docs.',
        cost: 14_000,
        apply: (s) => {
          s.player.reputation = clamp(s.player.reputation + 2, 0, 100);
          pushMod(s, 'cogsMult', { mult: 0.985, days: 45, label: 'Clean audit fast-lane' });
          return 'Audit passed. Your entries move through exam queues faster.';
        },
      },
      {
        id: 'risk',
        label: 'Risk it',
        detail: 'Save the money, hope for the best.',
        apply: () => 'You roll the dice. So far, so good… probably.',
      },
    ],
  },
  {
    id: 'tariff-turbulence-2025',
    title: 'Trade-War Escalation: New Tariff Rounds',
    body: 'A new round of trade measures lands on Asian imports. Effective duty burdens climb across China AND Southeast Asia. Sourcing "arbitrage" just got harder for everyone.',
    category: 'trade',
    severity: 4,
    durationDays: 0,
    window: [380, 430],
    onceOnly: true,
    weight: always,
    apply: (s) => {
      s.tariffs.CN += 0.2;
      s.tariffs.VN += 0.1;
      s.world.competition = clamp(s.world.competition + 0.05, 0.6, 1.85);
      news(s, 'trade', 4, 'Tariff shockwave hits Asian imports', 'China +20%, Vietnam +10% duty burden. Every competitor is scrambling.');
    },
    choices: [
      {
        id: 'absorb',
        label: 'Absorb and re-plan',
        detail: 'The whole market is in the same boat.',
        apply: () => 'Everyone absorbs together — for now. Weak brands will die first.',
      },
      {
        id: 'price',
        label: 'Industry-leading price increase',
        detail: '+6% prices, -8% demand for 90 days.',
        apply: (s) => {
          for (const p of activeProducts(s)) p.price = Math.round(p.price * 1.06);
          pushMod(s, 'demandMult', { mult: 0.92, days: 90, label: 'Post-tariff pricing' });
          return 'You raise prices first. Competitors follow within weeks — you kept the margin.';
        },
      },
    ],
  },

  // ======================= MACRO & COMMODITIES =======================
  {
    id: 'oil-shock',
    title: 'Oil Shock: Geopolitical Supply Scare',
    body: 'Crude spikes on supply-route tensions. Ocean freight and parcel fuel surcharges will follow with a 3–8 week lag. Foam feedstock costs firm up too.',
    category: 'macro',
    severity: 3,
    durationDays: 0,
    cooldownDays: 120,
    weight: () => 2.5,
    apply: (s, ctx) => {
      const spike = 1.18 + ctx.rng() * 0.2;
      s.world.oil = clamp(s.world.oil * spike, 42, 135);
    },
    choices: [
      {
        id: 'hedge',
        label: 'Lock a freight contract now',
        detail: '$16,000 — cap surcharge exposure for 60 days.',
        cost: 16_000,
        apply: (s) => {
          pushMod(s, 'freightMult', { mult: 0.96, days: 60, label: 'Locked freight contract' });
          return 'Freight capacity locked pre-spike. Competitors eat the surcharge; you mostly dodged it.';
        },
      },
      {
        id: 'absorb',
        label: 'Ride it out',
        detail: 'Surcharges arrive with the lag.',
        apply: () => 'You wait. The surcharge invoices will find you in a few weeks.',
      },
    ],
  },
  {
    id: 'oil-crash',
    title: 'Oil Slides on Demand Fears',
    body: 'Crude tumbles. Fuel surcharges will decay over the coming weeks — a rare tailwind for logistics-heavy brands.',
    category: 'macro',
    severity: 1,
    durationDays: 0,
    cooldownDays: 150,
    weight: () => 1.5,
    apply: (s) => {
      s.world.oil = clamp(s.world.oil * 0.82, 42, 135);
      news(s, 'macro', 1, 'Oil crashes — freight relief incoming', 'Fuel surcharges decay with a lag. Enjoy the margin breather.');
    },
  },
  {
    id: 'rate-cut',
    title: 'Fed Cuts Rates: Consumer Breathes',
    body: 'Rate cuts revive big-ticket purchasing. Mattress demand — a classic deferral purchase — ticks up industry-wide.',
    category: 'macro',
    severity: 1,
    durationDays: 90,
    cooldownDays: 300,
    weight: (s) => (s.time.day > 300 ? 2 : 0),
    apply: (s) => {
      pushMod(s, 'demandMult', { mult: 1.06, days: 90, label: 'Rate-cut consumer tailwind' });
      news(s, 'macro', 1, 'Rate cuts lift big-ticket demand', 'Mattresses are back on the shopping list.');
    },
  },
  {
    id: 'consumer-pinched',
    title: 'Inflation Squeezes the Consumer',
    body: 'Rent and groceries are eating paychecks. Big-ticket purchases get deferred; promo-hunting behavior spikes across the category.',
    category: 'macro',
    severity: 2,
    durationDays: 75,
    cooldownDays: 180,
    weight: () => 2,
    apply: (s) => {
      pushMod(s, 'demandMult', { mult: 0.93, days: 75, label: 'Pinched consumer' });
    },
    choices: [
      {
        id: 'value-bundle',
        label: 'Launch value bundle promo',
        detail: '$9,000 — protect volume with bundles.',
        cost: 9_000,
        apply: (s) => {
          pushMod(s, 'conversionMult', { mult: 1.05, days: 75, label: 'Value bundle promo' });
          pushMod(s, 'returnRateAdd', { add: 0.004, days: 75, label: 'Promo-driven mismatch returns' });
          return 'Bundles defend volume — with a small return-rate tax.';
        },
      },
      { id: 'hold', label: 'Hold premium position', detail: 'Weather the dip.', apply: () => 'You hold. Volume sags; brand equity intact.' },
    ],
  },
  {
    id: 'polyol-shortage',
    title: 'Polyol Shortage: Foam Feedstock Squeeze',
    body: 'A force-majeure declaration at a major polyol plant tightens foam chemistry supply worldwide. Foam index jumps; every mattress maker feels it.',
    category: 'supply',
    severity: 3,
    durationDays: 60,
    cooldownDays: 200,
    weight: () => 2,
    apply: (s) => {
      const halved = has(s, 'vertical-foam');
      pushMod(s, 'foamAdd', { add: halved ? 12 : 25, days: 60, label: 'Polyol shortage', source: 'polyol-shortage' });
    },
    choices: [
      {
        id: 'lock',
        label: 'Lock supply contract',
        detail: '$20,000 — secure foam at today\'s index.',
        cost: 20_000,
        apply: (s) => {
          pushMod(s, 'foamAdd', { add: -12, days: 60, label: 'Locked foam contract' });
          return 'Contract locked. Your foam costs stay (mostly) sane while spot buyers panic.';
        },
      },
      { id: 'spot', label: 'Buy at spot', detail: 'Pay the market.', apply: () => 'Spot market it is. Expensive foam, no commitments.' },
    ],
  },

  // ======================= COMPETITION =======================
  {
    id: 'price-war',
    title: 'Rival Launches 50%-Off Blitz + 365-Night Trial',
    body: 'A well-funded competitor goes scorched-earth: half off everything, a full-year trial, podcast ads everywhere. The category holds its breath.',
    category: 'competition',
    severity: 3,
    durationDays: 30,
    cooldownDays: 130,
    weight: (s) => (s.world.competition > 0.9 ? 3 : 1),
    apply: (s) => {
      pushMod(s, 'demandMult', { mult: 0.88, days: 30, label: 'Competitor 50%-off blitz', source: 'price-war' });
    },
    choices: [
      {
        id: 'match',
        label: 'Match with a sitewide sale',
        detail: 'Defend volume. Deal-hunters return more.',
        apply: (s) => {
          pushMod(s, 'demandMult', { mult: 1.1, days: 30, label: 'Matching sale' });
          pushMod(s, 'returnRateAdd', { add: 0.006, days: 30, label: 'Sale-driven mismatch returns' });
          pushMod(s, 'conversionMult', { mult: 1.04, days: 30, label: 'Sale urgency' });
          return 'Sale matched. Volume defended; margins and returns take the hit.';
        },
      },
      {
        id: 'counter-position',
        label: 'Counter-position: "We don\'t do fake 50% off"',
        detail: '$12,000 honest-pricing campaign.',
        cost: 12_000,
        apply: (s) => {
          pushMod(s, 'conversionMult', { mult: 1.06, days: 45, label: 'Honest-pricing campaign' });
          s.player.reputation = clamp(s.player.reputation + 3, 0, 100);
          return 'The honest-pricing angle lands. Slower week, stronger brand.';
        },
      },
      { id: 'ignore', label: 'Stay the course', detail: 'Blitzes burn out.', apply: () => 'You wait them out. Cash-burning rivals eventually blink.' },
    ],
  },
  {
    id: 'new-entrant',
    title: 'VC-Backed Startup Enters the Category',
    body: 'A $30M-funded startup launches with a celebrity investor and subway ads. Customer acquisition costs tick up for everyone as they bid up inventory.',
    category: 'competition',
    severity: 2,
    durationDays: 60,
    cooldownDays: 160,
    weight: () => 2.5,
    apply: (s) => {
      s.world.competition = clamp(s.world.competition + 0.07, 0.6, 1.85);
      pushMod(s, 'cacMult', { mult: 1.05, days: 60, label: 'New entrant bidding up ads', source: 'new-entrant' });
    },
  },
  {
    id: 'competitor-dies',
    title: 'Rival Brand Shutters Suddenly',
    body: 'Another DTC mattress brand runs out of runway — the classic postscript: high CAC, brutal returns, cash gone. Their customers are shopping for a new bed.',
    category: 'positive',
    severity: 1,
    durationDays: 45,
    cooldownDays: 200,
    weight: (s) => (s.time.day > 150 ? 2 : 0),
    apply: (s) => {
      s.world.competition = clamp(s.world.competition - 0.06, 0.6, 1.85);
      pushMod(s, 'demandMult', { mult: 1.05, days: 45, label: 'Displaced rival customers' });
      news(s, 'competition', 1, 'A rival brand shuts down', 'Orphaned customers are up for grabs.');
    },
  },
  {
    id: 'cac-inflation',
    title: 'Ad-Auction Inflation: CACs Jump',
    body: 'Privacy changes and new category entrants push Meta and Google auction prices up double digits. Every DTC brand\'s payback math just got worse.',
    category: 'competition',
    severity: 2,
    durationDays: 90,
    cooldownDays: 170,
    weight: (s) => (s.marketing.enabled && s.marketing.dailyBudget > 150 ? 2.5 : 0),
    apply: (s) => {
      pushMod(s, 'cacMult', { mult: 1.08, days: 90, label: 'Ad auction inflation', source: 'cac-inflation' });
    },
    choices: [
      {
        id: 'creative',
        label: 'Fund a creative sprint',
        detail: '$11,000 — fresh angles beat the algorithm.',
        cost: 11_000,
        apply: (s) => {
          for (const c of ['meta', 'google', 'tiktok'] as MarketingChannelId[]) {
            s.marketing.fatigue[c] = Math.max(0, s.marketing.fatigue[c] - 0.35);
          }
          return 'Fresh creative ships. Fatigue resets, CPA normalizes somewhat.';
        },
      },
      { id: 'absorb', label: 'Absorb higher CAC', detail: 'Payback periods stretch.', apply: () => 'You absorb it. Payback math gets uncomfortable.' },
    ],
  },
  {
    id: 'pe-rollup',
    title: 'Private Equity Rolls Up Three Brands',
    body: 'A PE platform acquires three mid-tier mattress brands and merges operations. Procurement synergies mean aggressive pricing power — and a new aggressive ad buyer.',
    category: 'competition',
    severity: 2,
    durationDays: 60,
    cooldownDays: 250,
    weight: (s) => (s.time.day > 365 ? 2 : 0),
    apply: (s) => {
      s.world.competition = clamp(s.world.competition + 0.05, 0.6, 1.85);
      pushMod(s, 'cacMult', { mult: 1.04, days: 60, label: 'PE rollup ad pressure' });
    },
  },

  // ======================= PLATFORM (AMAZON) =======================
  {
    id: 'amazon-algo',
    title: 'Amazon Overhauls Mattress Category Ranking',
    body: 'An algorithm update reshuffles mattress search results. Listings heavy on returns and slow shipping get buried. Rank volatility spikes for everyone.',
    category: 'platform',
    severity: 3,
    durationDays: 21,
    cooldownDays: 150,
    weight: (s) => wAmazon(s) * 2.5,
    apply: (s) => {
      pushMod(s, 'amazonTrafficMult', { mult: 0.82, days: 21, label: 'A9/A10 algo turbulence', source: 'amazon-algo' });
      s.channels.amazon.organicRankScore = Math.max(2, s.channels.amazon.organicRankScore * 0.88);
    },
    choices: [
      {
        id: 'optimize',
        label: 'Emergency listing optimization',
        detail: '$9,000 — A+ content, images, keywords.',
        cost: 9_000,
        apply: (s) => {
          s.channels.amazon.organicRankScore = Math.min(100, s.channels.amazon.organicRankScore * 1.06);
          return 'Listings rebuilt to the new playbook. You recover faster than most.';
        },
      },
      { id: 'wait', label: 'Wait for the dust to settle', detail: 'Ride the volatility.', apply: () => 'You wait. Rank swings; sales dip.' },
    ],
  },
  {
    id: 'amazon-fees',
    title: 'Amazon Raises FBA Fee Schedule',
    body: 'Fulfillment and referral fees rise again — "inflation adjustment," they call it. The take-rate on your Amazon business inches up. It only ever goes one way.',
    category: 'platform',
    severity: 2,
    durationDays: 0,
    cooldownDays: 240,
    weight: (s) => wAmazon(s) * 2,
    apply: (s) => {
      s.channels.amazon.feePercent = Math.min(0.22, s.channels.amazon.feePercent + 0.012);
      s.channels.amazon.fulfillmentPerUnit += 3;
      news(s, 'platform', 2, 'Amazon fee schedule increases', 'Take-rate up ~1.2pt + $3/unit fulfillment.');
    },
  },
  {
    id: 'amazon-review-purge',
    title: 'Amazon Purges Suspected Incentivized Reviews',
    body: 'A sweep wipes thousands of category reviews, including some legit ones. Review counts drop; conversion wobbles.',
    category: 'platform',
    severity: 2,
    durationDays: 0,
    cooldownDays: 200,
    weight: (s) => wAmazon(s) * (activeProducts(s).some((p) => p.reviews.count > 80) ? 2 : 0),
    apply: (s) => {
      for (const p of activeProducts(s)) p.reviews.count = Math.round(p.reviews.count * 0.85);
    },
    choices: [
      {
        id: 'vine',
        label: 'Enroll in Review Accelerator',
        detail: '$7,500 — legit review pipeline.',
        cost: 7_500,
        apply: (s) => {
          for (const p of activeProducts(s)) p.reviews.count += 12;
          return 'Review velocity recovers within weeks.';
        },
      },
      { id: 'wait', label: 'Let it rebuild organically', detail: 'Slower but free.', apply: () => 'Reviews rebuild slowly. Trust scores dip for now.' },
    ],
  },
  {
    id: 'amazon-suspension',
    title: 'Amazon Account Health Warning',
    body: 'A listing-compliance sweep flags your account. Amazon suspension risk: if the account goes dark, your largest channel goes to zero overnight.',
    category: 'platform',
    severity: 4,
    durationDays: 0,
    cooldownDays: 300,
    weight: (s) => wAmazon(s) * (s.time.day > 120 ? 1.5 : 0),
    apply: () => {},
    choices: [
      {
        id: 'appeal',
        label: 'File appeal package (consultant)',
        detail: '$12,000 — account stays up, probation 10 days.',
        cost: 12_000,
        apply: (s) => {
          s.channels.amazon.sellerRating = Math.max(20, s.channels.amazon.sellerRating - 6);
          pushMod(s, 'amazonTrafficMult', { mult: 0.92, days: 10, label: 'Account probation' });
          return 'Appeal accepted. Account survives with a traffic bruise.';
        },
      },
      {
        id: 'ride',
        label: 'Respond yourself and hope',
        detail: '50/50: suspension for 10 days.',
        apply: (s, ctx) => {
          if (ctx.rng() < 0.5) {
            s.channels.amazon.suspended = true;
            // Marker the engine consumes to auto-reinstate after 10 days.
            s.activeEvents.push({ defId: '__amazon_unsuspend', startedDay: s.time.day, expiresDay: s.time.day + 10, choiceTaken: null });
            return 'The appeal fails. Amazon goes dark for 10 days.';
          }
          s.channels.amazon.sellerRating = Math.max(20, s.channels.amazon.sellerRating - 3);
          return 'Your DIY appeal works. Close call.';
        },
      },
    ],
  },
  {
    id: 'meta-cpm',
    title: 'iOS Privacy Changes Hammer Ad Targeting',
    body: 'Mobile privacy changes gut attribution and targeting quality. Meta CPMs spike as efficiency drops. Every DTC marketer re-learns "diversification" this quarter.',
    category: 'platform',
    severity: 3,
    durationDays: 75,
    cooldownDays: 260,
    weight: (s) => (s.marketing.allocation.meta > 0.05 ? 2.5 : 0),
    apply: (s) => {
      pushMod(s, 'cacMult', { mult: 1.09, days: 75, label: 'Post-privacy CPM inflation', source: 'meta-cpm' });
    },
    choices: [
      {
        id: 'diversify',
        label: 'Rebalance budget to TikTok/email',
        detail: 'Shift 15% of budget: TikTok +10%, Email +5%.',
        apply: (s) => {
          const a = s.marketing.allocation;
          const shift = Math.min(0.15, Math.max(0, a.meta - 0.05));
          a.meta = Math.max(0, a.meta - shift);
          a.tiktok = clamp(a.tiktok + shift * 0.66, 0, 1);
          a.email = clamp(a.email + shift * 0.34, 0, 1);
          return 'Budget rebalanced. Less targeting pain, more creative volatility.';
        },
      },
      { id: 'hold', label: 'Keep the mix', detail: 'Efficiency dips.', apply: () => 'You keep spending. CAC climbs for a quarter.' },
    ],
  },

  // ======================= VIRAL / SOCIAL =======================
  {
    id: 'viral-returns',
    title: 'Viral TikTok: "I Returned 9 Mattresses"',
    body: 'A creator\'s mattress-return exposé goes viral — warehouse piles of used returns, refund battles, the works. Trial-heavy brands (that\'s you) catch strays. Return requests spike.',
    category: 'viral',
    severity: 3,
    durationDays: 30,
    cooldownDays: 170,
    weight: (s) => (s.player.awareness > 18 ? 2.5 : 0.5),
    apply: (s) => {
      pushMod(s, 'returnRateAdd', { add: 0.03, days: 30, label: 'Viral return-ophobia', source: 'viral-returns' });
      s.player.reputation = clamp(s.player.reputation - 5, 0, 100);
    },
    choices: [
      {
        id: 'respond',
        label: 'Transparency response campaign',
        detail: '$16,000 — show your return process honestly.',
        cost: 16_000,
        apply: (s) => {
          pushMod(s, 'returnRateAdd', { add: -0.015, days: 30, label: 'Trust-building response' });
          s.player.reputation = clamp(s.player.reputation + 4, 0, 100);
          return 'Your transparency video outperforms the outrage. Brand strengthened.';
        },
      },
      { id: 'ignore', label: 'Say nothing', detail: 'News cycles move on.', apply: () => 'You stay silent. The cycle passes; some damage sticks.' },
    ],
  },
  {
    id: 'viral-unboxing',
    title: 'Celebrity Unboxing Goes Viral',
    body: 'A major creator films your mattress unboxing "for the plot" — and the internet falls in love. Traffic tsunami incoming.',
    category: 'positive',
    severity: 2,
    durationDays: 21,
    cooldownDays: 220,
    weight: (s) => (s.marketing.allocation.tiktok > 0.05 ? 2 : 0.6),
    apply: (s) => {
      pushMod(s, 'demandMult', { mult: 1.12, days: 21, label: 'Viral unboxing', source: 'viral-unboxing' });
      pushMod(s, 'awarenessAddPerDay', { add: 0.5, days: 21, label: 'Viral awareness burst' });
      news(s, 'viral', 2, 'Your mattress goes viral', 'Unboxing views are exploding. Inventory, meet demand.');
    },
  },
  {
    id: 'influencer-fraud',
    title: 'Influencer Agency Scandal',
    body: 'A big "performance" influencer agency is exposed for fake engagement metrics. Brands that paid them — possibly including yours — burned budget on bots.',
    category: 'viral',
    severity: 2,
    durationDays: 30,
    cooldownDays: 240,
    weight: (s) => wInfluencerSpend(s) * 2,
    apply: (s) => {
      pushMod(s, 'cacMult', { mult: 1.06, days: 30, label: 'Influencer budget burn', source: 'influencer-fraud' });
      s.marketing.fatigue.influencer = clamp(s.marketing.fatigue.influencer + 0.2, 0, 1);
    },
  },
  {
    id: 'celebrity-offer',
    title: 'Celebrity Endorsement Opportunity',
    body: 'A-list talent (with a genuine sleep story) is available for a 12-month endorsement. Their team wants $60,000 upfront. Big reach, big check.',
    category: 'opportunity',
    severity: 2,
    durationDays: 0,
    cooldownDays: 300,
    weight: (s) => (s.player.cash > 90_000 && s.time.day > 200 ? 1.5 : 0),
    apply: () => {},
    choices: [
      {
        id: 'sign',
        label: 'Sign the deal',
        detail: '$60,000 — +10 awareness, +5% conversion for 90 days.',
        cost: 60_000,
        apply: (s) => {
          s.player.awareness = clamp(s.player.awareness + 10, 0, 100);
          pushMod(s, 'conversionMult', { mult: 1.05, days: 90, label: 'Celebrity halo' });
          return 'The campaign launches. Awareness jumps overnight.';
        },
      },
      { id: 'pass', label: 'Pass', detail: 'Keep the cash.', apply: () => 'You pass. The tabloids will survive without you.' },
    ],
  },
  {
    id: 'sleep-documentary',
    title: 'Sleep-Health Documentary Hits the Top 10',
    body: 'A streaming documentary about the sleep-deprivation epidemic becomes a national conversation. Category interest surges — "sleepmaximizing" is suddenly a word.',
    category: 'positive',
    severity: 1,
    durationDays: 45,
    cooldownDays: 260,
    weight: () => 1.8,
    apply: (s) => {
      pushMod(s, 'demandMult', { mult: 1.06, days: 45, label: 'Sleep-doc category buzz' });
      pushMod(s, 'awarenessAddPerDay', { add: 0.15, days: 45, label: 'Category halo' });
    },
  },

  // ======================= SUPPLY CHAIN =======================
  {
    id: 'port-strike',
    title: 'Port Labor Action Snarls Imports',
    body: 'Dockworker negotiations collapse. West Coast ports slow to a crawl; ocean transit times balloon. Anything on the water is late; anything new pays a premium.',
    category: 'supply',
    severity: 3,
    durationDays: 45,
    cooldownDays: 220,
    weight: (s) => (s.suppliersUnlocked.some((x) => x !== 'georgia-mattress-co') ? 2.5 : 0),
    apply: (s) => {
      pushMod(s, 'freightMult', { mult: 1.15, days: 45, label: 'Port congestion', source: 'port-strike' });
    },
    choices: [
      {
        id: 'expedite',
        label: 'Expedite via air/rail blend',
        detail: '$24,000 — keep product flowing.',
        cost: 24_000,
        apply: (s) => {
          pushMod(s, 'freightMult', { mult: 0.93, days: 45, label: 'Expedited lanes' });
          return 'Expedited lanes keep you in stock while rivals wait on the water.';
        },
      },
      { id: 'wait', label: 'Wait it out', detail: 'Slower, cheaper.', apply: () => 'You wait. Inventory gets thin; surcharges pile up.' },
    ],
  },
  {
    id: 'supplier-quality',
    title: 'Supplier Quality Slip Detected',
    body: 'Your QC sampling finds foam density drift and sloppy seam welding in recent production. If it ships, customers will feel it — and review it.',
    category: 'supply',
    severity: 3,
    durationDays: 30,
    cooldownDays: 190,
    weight: (s) => (s.pos.length > 0 || Object.values(s.inventory).some((v) => v > 100) ? 2 : 0),
    apply: (s) => {
      pushMod(s, 'returnRateAdd', { add: 0.02, days: 30, label: 'Defective batch returns', source: 'supplier-quality' });
      s.player.reputation = clamp(s.player.reputation - 3, 0, 100);
    },
    choices: [
      {
        id: 'inspect',
        label: 'Third-party inspection + rework',
        detail: '$13,000 — catch defects before customers do.',
        cost: 13_000,
        apply: (s) => {
          pushMod(s, 'returnRateAdd', { add: -0.012, days: 30, label: 'QC rework program' });
          s.player.reputation = clamp(s.player.reputation + 2, 0, 100);
          return 'Inspection catches the batch. Rework saves your reviews.';
        },
      },
      {
        id: 'switch',
        label: 'Threaten to move the POs',
        detail: 'Free — supplier scrambles to fix it (50/50).',
        apply: (s, ctx) => {
          if (ctx.rng() < 0.5) {
            pushMod(s, 'returnRateAdd', { add: -0.018, days: 30, label: 'Supplier corrective action' });
            return 'The supplier panics and fixes the line. Quality restored.';
          }
          pushMod(s, 'returnRateAdd', { add: 0.008, days: 30, label: 'Quality still slipping' });
          return 'They promise fixes; the next batch is… not fixed.';
        },
      },
    ],
  },
  {
    id: 'trucking-crunch',
    title: 'Parcel Capacity Crunch',
    body: 'Peak-season parcel capacity tightens; carriers impose demand surcharges. Outbound costs jump for everyone shipping big boxes.',
    category: 'supply',
    severity: 2,
    durationDays: 30,
    cooldownDays: 170,
    weight: () => 2,
    apply: (s) => {
      pushMod(s, 'freightMult', { mult: 1.07, days: 30, label: 'Parcel demand surcharge' });
    },
  },
  {
    id: 'warehouse-rent',
    title: 'Warehouse Rent Renewal Sticker Shock',
    body: 'Industrial rents are up double digits. Your 3PL sends the renewal: pay up, or migrate inventory (which costs too).',
    category: 'supply',
    severity: 1,
    durationDays: 0,
    cooldownDays: 300,
    weight: (s) => (s.time.day > 180 ? 1.8 : 0),
    apply: () => {},
    choices: [
      {
        id: 'accept',
        label: 'Accept the increase',
        detail: '+$55/day overhead, permanent.',
        apply: (s) => {
          pushMod(s, 'overheadAdd', { add: 55, days: 9999, label: 'Warehouse rent increase' });
          return 'Rent increased. The landlord sends a fruit basket.';
        },
      },
      {
        id: 'renegotiate',
        label: 'Play hardball',
        detail: '$8,000 legal/consulting — likely halves it.',
        cost: 8_000,
        apply: (s, ctx) => {
          if (ctx.rng() < 0.65) {
            pushMod(s, 'overheadAdd', { add: 25, days: 9999, label: 'Renegotiated rent' });
            return 'Renegotiated to a smaller increase.';
          }
          pushMod(s, 'overheadAdd', { add: 60, days: 9999, label: 'Rent increase (failed negotiation)' });
          return 'Hardball fails; they call your bluff. Full increase plus fees.';
        },
      },
    ],
  },

  // ======================= REGULATORY =======================
  {
    id: 'flammability-testing',
    title: 'Flammability Standard Testing Update',
    body: 'Regulators refresh federal flammability (16 CFR 1632/1633) documentation requirements. New batch testing is required to keep selling — no shortcuts.',
    category: 'regulatory',
    severity: 2,
    durationDays: 0,
    cooldownDays: 320,
    weight: (s) => (activeProducts(s).length > 0 ? 2 : 0),
    apply: () => {},
    choices: [
      {
        id: 'test',
        label: 'Full compliance testing',
        detail: '$15,000 — certified and done.',
        cost: 15_000,
        apply: (s) => {
          s.player.reputation = clamp(s.player.reputation + 1, 0, 100);
          return 'Testing complete, paperwork filed. Selling legally feels good.';
        },
      },
      {
        id: 'delay',
        label: 'Ship and hope (compliance risk)',
        detail: 'Risk: returns spike, reputation hit.',
        apply: (s, ctx) => {
          if (ctx.rng() < 0.4) {
            pushMod(s, 'returnRateAdd', { add: 0.015, days: 60, label: 'Compliance scare returns' });
            s.player.reputation = clamp(s.player.reputation - 4, 0, 100);
            return 'A marketplace delisting scare hits. Returns and trust take the hit.';
          }
          return 'Nobody notices. This time.';
        },
      },
    ],
  },
  {
    id: 'recycling-epr',
    title: 'Mattress Recycling Law (EPR) Passes',
    body: 'Extended producer responsibility legislation passes: brands fund mattress recycling. A small per-unit eco fee becomes permanent overhead.',
    category: 'regulatory',
    severity: 1,
    durationDays: 0,
    cooldownDays: 400,
    weight: (s) => (s.time.day > 300 ? 1.5 : 0),
    apply: () => {},
    choices: [
      {
        id: 'comply',
        label: 'Comply quietly',
        detail: '+$30/day overhead, permanent.',
        apply: (s) => {
          pushMod(s, 'overheadAdd', { add: 30, days: 9999, label: 'EPR recycling fees' });
          return 'EPR fees flow. Marketing turns compliance into an eco badge.';
        },
      },
      {
        id: 'market',
        label: 'Comply + "Take-Back" program marketing',
        detail: '$10,000 — turn the fee into a conversion story.',
        cost: 10_000,
        apply: (s) => {
          pushMod(s, 'overheadAdd', { add: 30, days: 9999, label: 'EPR recycling fees' });
          pushMod(s, 'conversionMult', { mult: 1.03, days: 120, label: 'Take-back program halo' });
          s.player.reputation = clamp(s.player.reputation + 3, 0, 100);
          return 'The take-back program headlines your sustainability page. Fees become PR.';
        },
      },
    ],
  },

  // ======================= OPPORTUNITY / SCALE =======================
  {
    id: 'award-win',
    title: '"Best Mattress" Award Win',
    body: 'A major sleep publication names your flagship a top pick. The badge is marketing gold — if you move fast.',
    category: 'positive',
    severity: 2,
    durationDays: 30,
    cooldownDays: 240,
    weight: (s) => (activeProducts(s).some((p) => p.reviews.rating >= 4.4 && p.reviews.count > 60) ? 2.2 : 0),
    apply: (s) => {
      pushMod(s, 'demandMult', { mult: 1.07, days: 30, label: 'Award badge' });
      s.player.awareness = clamp(s.player.awareness + 4, 0, 100);
      news(s, 'positive', 2, 'Award win!', 'Best-of badges drive a conversion bump.');
    },
  },
  {
    id: 'hotel-contract',
    title: 'Boutique Hotel Chain Wants a Contract',
    body: 'A boutique hotel group wants custom-labeled mattresses for a 40-room property. Wholesale pricing, one big PO, net-60 payment. Requires finished inventory.',
    category: 'opportunity',
    severity: 2,
    durationDays: 0,
    cooldownDays: 280,
    weight: (s) => wWholesale(s) * 2,
    apply: () => {},
    choices: [
      {
        id: 'accept',
        label: 'Take the order',
        detail: 'Consumes 120 units of inventory; books ~45% of retail to receivables.',
        apply: (s) => {
          const act = activeProducts(s);
          const totalInv = act.reduce((a, p) => a + (s.inventory[p.id] ?? 0), 0);
          if (totalInv < 120) return 'Not enough finished inventory to commit. The hotel signs with a competitor.';
          // Pull from the deepest-stocked SKUs.
          let need = 120;
          let value = 0;
          for (const p of [...act].sort((a, b) => (s.inventory[b.id] ?? 0) - (s.inventory[a.id] ?? 0))) {
            const take = Math.min(need, s.inventory[p.id] ?? 0);
            s.inventory[p.id] -= take;
            value += take * p.price * 0.55;
            need -= take;
            if (need <= 0) break;
          }
          s.channels.wholesale.receivables += value;
          news(s, 'finance', 2, 'Hotel contract signed', `~$${Math.round(value).toLocaleString()} booked to receivables (net-60).`);
          return 'Order fulfilled to the hotel dock. Cash arrives in ~60 days.';
        },
      },
      { id: 'pass', label: 'Pass', detail: 'Too operational.', apply: () => 'You pass. DTC focus stays laser.' },
    ],
  },
  {
    id: 'copycat-listing',
    title: 'Counterfeit Knockoff Appears on Your Listing',
    body: 'A counterfeit seller hijacks a variant of your best Amazon listing with a cheap knockoff. Your reviews now describe someone else\'s terrible bed.',
    category: 'platform',
    severity: 2,
    durationDays: 25,
    cooldownDays: 210,
    weight: (s) => wAmazon(s) * (activeProducts(s).some((p) => p.reviews.count > 120) ? 1.8 : 0),
    apply: (s) => {
      pushMod(s, 'conversionMult', { mult: 0.94, days: 25, label: 'Counterfeit listing damage', source: 'copycat-listing' });
      for (const p of activeProducts(s)) if (p.reviews.count > 120) p.reviews.rating = Math.max(3.2, p.reviews.rating - 0.15);
    },
    choices: [
      {
        id: 'brand-registry',
        label: 'Brand Registry takedown + legal',
        detail: '$6,500 — remove it fast.',
        cost: 6_500,
        apply: () => 'Takedown filed. The knockoff vanishes within days.',
      },
      { id: 'wait', label: 'Report and wait', detail: 'Slow marketplace process.', apply: () => 'The case crawls through support. Damage accrues.' },
    ],
  },
  {
    id: 'cyber-incident',
    title: 'E-Commerce Platform Outage',
    body: 'A botched platform release takes checkout down for many DTC brands for ~48 hours. Sessions don\'t convert; support tickets pile up.',
    category: 'platform',
    severity: 2,
    durationDays: 3,
    cooldownDays: 260,
    weight: (s) => wShopifyScale(s) * 1.5,
    apply: (s) => {
      pushMod(s, 'shopifyTrafficMult', { mult: 0.35, days: 3, label: 'Checkout outage', source: 'cyber-incident' });
    },
  },
  {
    id: 'de-minimis-crackdown',
    title: 'De Minimis Loophole Closes',
    body: 'The duty-free de minimis exemption gets tightened; customs enforcement surges. Cheap direct-from-Asia competitors lose their edge — but your brokerage costs rise too.',
    category: 'trade',
    severity: 2,
    durationDays: 0,
    window: [660, 720],
    onceOnly: true,
    weight: always,
    apply: (s) => {
      pushMod(s, 'overheadAdd', { add: 25, days: 9999, label: 'Customs brokerage costs' });
      pushMod(s, 'freightMult', { mult: 1.04, days: 60, label: 'Exam backlogs' });
      news(s, 'trade', 2, 'De minimis crackdown reshapes imports', 'Duty-free small-parcel loophole curtailed.');
    },
  },
];

/** Scripted one-timers that always fire in their window (narrative spine). */
export function findScriptedEvent(s: GameState): EventDef | null {
  for (const e of EVENTS) {
    if (!e.window || !e.onceOnly) continue;
    if (s.firedOnceEvents.includes(e.id)) continue;
    const [a, b] = e.window;
    if (s.time.day >= a && s.time.day <= b) return e;
  }
  return null;
}

export const EVENT_BY_ID: Record<string, EventDef> = Object.fromEntries(EVENTS.map((e) => [e.id, e]));

/** Roll for a random event today. Returns the def or null. */
export function rollRandomEvent(s: GameState, rng: () => number, severityMult: number): EventDef | null {
  const pool: EventDef[] = [];
  let total = 0;
  for (const e of EVENTS) {
    if (e.window) continue; // scripted handled separately
    if (s.eventCooldowns[e.id] && s.eventCooldowns[e.id] > s.time.day) continue;
    if (e.onceOnly && s.firedOnceEvents.includes(e.id)) continue;
    const w = e.weight(s);
    if (w <= 0) continue;
    pool.push(e);
    total += w;
  }
  if (pool.length === 0) return null;
  // Base cadence: ~1 event per 2–3.5 weeks depending on difficulty pressure.
  // BALANCE (v2): 0.0011 → 0.0014 — mid/late game with more systems online
  // should feel lively; early game stays calm because weights gate the pool.
  const chance = clamp(total * 0.0014 * severityMult, 0.01, 0.5);
  if (rng() > chance) return null;
  let r = rng() * total;
  for (const e of pool) {
    const w = e.weight(s);
    if (r < w) return e;
    r -= w;
  }
  return pool[pool.length - 1] ?? null;
}

/** Fire an event: apply immediate effects, register active state, queue modal. */
export function fireEvent(s: GameState, def: EventDef, rng: () => number): void {
  const active: ActiveEvent = {
    defId: def.id,
    startedDay: s.time.day,
    expiresDay: def.durationDays > 0 ? s.time.day + def.durationDays : s.time.day,
    choiceTaken: null,
  };
  s.activeEvents.push(active);
  if (def.onceOnly) s.firedOnceEvents.push(def.id);
  s.eventCooldowns[def.id] = s.time.day + (def.cooldownDays ?? 60);
  def.apply(s, { rng, day: s.time.day });
  news(s, def.category, def.severity, def.title, def.body);
  // Modal for anything with choices or serious severity.
  if ((def.choices && def.choices.length > 0) || def.severity >= 3) {
    s.pendingEventId = def.id;
  }
}

/** Resolve the pending event with a choice (or acknowledge). */
export function resolvePendingEvent(s: GameState, choiceId: string | null, rng: () => number): string | null {
  const def = s.pendingEventId ? EVENT_BY_ID[s.pendingEventId] : null;
  s.pendingEventId = null;
  if (!def) return null;
  if (!choiceId) return null;
  const choice = def.choices?.find((c) => c.id === choiceId);
  if (!choice) return null;
  const cost = typeof choice.cost === 'function' ? choice.cost(s) : choice.cost;
  if (cost && s.player.cash < cost) return null;
  if (cost) s.player.cash -= cost;
  const active = s.activeEvents.find((a) => a.defId === def.id && a.choiceTaken === null);
  if (active) active.choiceTaken = choiceId;
  const outcome = choice.apply(s, { rng, day: s.time.day });
  news(s, def.category, def.severity, `Decision: ${choice.label}`, outcome);
  return outcome;
}

export type { EventChoiceDef };
